import {
  createHttpTransport,
  HttpStatusError,
  type HttpTransport,
} from "app-base/client/http"
import {
  createRealtimeClient,
  type RealtimeClient,
  type RealtimeSocket,
} from "app-base/client/realtime"
import { localStorageValue } from "./LocalStorage"
import { logger } from "./Logger"
import { SubscriptionMap, subscriptionStream } from "../Types/Generated/LogbookApi/Subscriptions"

export interface DataCallback {
  (data: object, xobj: XMLHttpRequest): void;
}

export interface ErrorCallback {
  (error: object, xobj: XMLHttpRequest): void;
}

export interface ApiProps {
  [key: string]: string | number | boolean | null | undefined;
}

export interface StreamCallback<T> {
  (object: T, type: string, info: string): void;
}

export interface SocketCallback {
  (connected: boolean, event: string): void;
}

function isHttpStatusError(
  error: unknown,
): error is HttpStatusError<unknown> {
  if (error instanceof HttpStatusError) {
    return true;
  }
  if (error == null || typeof error != "object") {
    return false;
  }
  const value = error as { name?: unknown; status?: unknown };
  return value.name == "HttpStatusError" && typeof value.status == "number";
}

export class Client {
  session?: string;
  disconnected = false;

  static actingShipUserId?: number;

  static setActingShipUserId(id?: number) {
    Client.actingShipUserId = id;
  }

  private streamTimeout = 1000;
  private readonly http: HttpTransport;
  private readonly realtime: RealtimeClient<Record<string, unknown>, number>;

  constructor(session?: string) {
    this.session = session || localStorageValue("logbook-session");
    this.http = createHttpTransport({
      fetch: (input, init) => window.fetch(input, init),
      resolveUrl: (path) => `/api/${path}`,
      headers: () => {
        const headers = new Headers();
        if (this.session) {
          headers.set("Authorization", `token ${this.session}`);
        }
        if (Client.actingShipUserId != undefined) {
          headers.set("X-Acting-Ship-User-Id", String(Client.actingShipUserId));
        }
        return headers;
      },
    });
    this.realtime = createRealtimeClient<Record<string, unknown>, number>({
      createUrl: ({ cursor, resuming }) => {
        if (!this.session) {
          return undefined;
        }
        const proto = document.location.protocol == "https:" ? "wss:" : "ws:";
        return proto + document.location.host + subscriptionStream.path
          + "?" + subscriptionStream.token + "=" + encodeURIComponent(this.session)
          + (resuming ? "&" + subscriptionStream.since + "=" + cursor : "");
      },
      createSocket: (url) => new window.WebSocket(url),
      reconnectDelay: () => this.streamTimeout,
      channelSubscriptionDelay: 100,
      socketOpenState: WebSocket.OPEN,
      initialCursor: undefined,
      parseData: (data) => JSON.parse(String(data)),
      onControl: (message) => {
        if (message.event == "replay-incomplete") {
          window.location.reload();
        } else if (message.event == "companion-disconnected") {
          this.endSession();
          this.trigger("CompanionDisconnected", {}, message.event);
        }
      },
      onConnection: ({ connected }) => {
        this.disconnected = !connected;
        this.realtime.trigger("socket", connected, connected ? "connect" : "disconnect");
      },
    });
  }

  get socket(): RealtimeSocket | undefined {
    return this.realtime.socket();
  }

  get maxId(): number {
    return this.realtime.resumeCursor() ?? 0;
  }

  set maxId(value: number) {
    this.realtime.setResumeCursor(value > 0 ? value : undefined);
  }

  async call<T>(method: string, action: string, props?: ApiProps, file?: File): Promise<T> {
    try {
      return await this.http.request<T>({
        method,
        path: action,
        query: method == "GET" && props
          ? Object.fromEntries(Object.entries(props).map(([key, value]) => [key, value ? value : ""]))
          : undefined,
        body: method != "GET" && !file ? props : undefined,
        multipart: file ? { attachment: { value: file, filename: file.name } } : undefined,
      });
    } catch (error) {
      if (!isHttpStatusError(error)) {
        throw error;
      }
      const data: Record<string, unknown> =
        error.body != null && typeof error.body == "object"
          ? { ...(error.body as Record<string, unknown>) }
          : {};
      data.httpStatus = error.status;
      throw data;
    }
  }

  stream(timeout: number = 1000) {
    this.streamTimeout = timeout;
    this.disconnected = false;
    this.realtime.connect();
  }

  endSession() {
    this.session = undefined;
    this.disconnected = true;
    this.realtime.disconnect();
  }

  subscribeChannel(channel: string) {
    this.realtime.subscribeChannel(channel);
  }

  unsubscribeChannel(channel: string) {
    this.realtime.unsubscribeChannel(channel);
  }

  subscribe<K extends keyof SubscriptionMap>(type: K, callback: StreamCallback<SubscriptionMap[K]>): number;
  subscribe<T>(type: string, callback: StreamCallback<T>): number;
  subscribe<T>(type: string, callback: StreamCallback<T>): number {
    return this.realtime.subscribe(
      type,
      (data, subscriptionType, event) => {
        const reportError = (error: unknown) => {
          logger.error("Realtime update failed", { type: subscriptionType, event }, error);
        };
        try {
          void Promise.resolve(callback(data as T, subscriptionType, event)).catch(reportError);
        } catch (error) {
          reportError(error);
        }
      },
    );
  }

  trigger<T>(type: string, data: T, info: string) {
    this.realtime.trigger(type, data, info);
  }

  unsubscribe(type: string, id: number) {
    this.realtime.unsubscribe(type, id);
  }

  subscribeSocket(callback: SocketCallback): number {
    return this.subscribe<boolean>("socket", (connected, _type, event) => callback(connected, event));
  }

  unsubscribeSocket(id: number) {
    this.unsubscribe("socket", id);
  }
}
